import React, {useEffect, useState} from 'react';
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import InputForm from "../../../components/input-form";
import SelectComponent from "../../../components/select-component";
import {useQueries, useQuery} from "@tanstack/react-query";
import UserService from "../../../services/user-service";
import userService from "../../../services/user-service";
import ProjectService from "../../../services/project-service";
import ResalesService from "../../../services/resales-service";
import Button from "../../../components/button";
import {toast} from "react-toastify";
import {MaskDefault} from "../../../utils/mask";
import {AlertMessage} from "../../../utils/alert-message";
import InputCheckbox from "../../../components/input-checkbox";
import {initialDataResource} from "../resources/initial-data";
import {permissionGroupLabels, permissionsList} from "../resources/permissionsList";
import {groupSshOptions} from "../resources/group-ssh-options";
import MySwal from "sweetalert2";
import GeneralUtils from "../../../utils/general";
import {useNavigate, useParams} from "react-router-dom";

const initialData = initialDataResource;
const permissionGroups = Object.keys(permissionGroupLabels).map((key) => ({
    key,
    title: permissionGroupLabels[key],
}));
const permissionsByKey = permissionsList.reduce((acc, permission) => {
    acc[permission.key] = permission;
    return acc;
}, {});
const mutuallyExclusivePermissions = {
    supervisor: ["see_attendances"],
    alter_status: ["see_attendances"],
    see_attendances: ["supervisor", "alter_status"],
    cv_candidates: ["cv_candidates_view_only"],
    cv_candidates_view_only: ["cv_candidates"],
};
const UsersForm = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(initialData);
    const [project, setProject] = useState("");
    const [cpfValidate, setCpfValidate] = useState(null);
    const [lastAccess, setLastAccess] = useState("");
    const [resale, setResale] = useState("");
    const [groupSsh, setGroupSsh] = useState("");
    const [permissions, setPermissions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState(null)
    const loggedUser = JSON.parse(window.localStorage.getItem("user") || "{}");
    const loggedUserPermissions = Array.isArray(loggedUser?.permissions)
        ? loggedUser.permissions
        : (loggedUser?.permissions ?? "").split(",");
    const canEditSsh = loggedUserPermissions.includes("can_edit_ssh");
    const canEditPermissions = loggedUserPermissions.includes("can_edit_permissions");
    const canEditSentryResale = loggedUserPermissions.includes("can_edit_sentry_resale");
    const selectedResaleId = Number(resale?.value ?? data.resale_id ?? 0);
    const cantEditFields = selectedResaleId === 1 && !canEditSentryResale;

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const queries = useQueries({
        queries: [
            {
                queryKey: ['projects', {id}],
                queryFn: () => {
                    return ProjectService.options().then(({data}) => data)
                },
            },
            {
                queryKey: ['resales', {id}],
                queryFn: () => {
                    return ResalesService.options().then(({data}) => data)
                },
            },
        ]
    });

    const projects = queries[0].data;
    const resales = queries[1].data;

    const {refetch} = useQuery({
        queryFn: () => UserService.getById(id).then((data) => {
            const {created_at_formated, user_created_name, updated_at_formated, user_updated_name} = data

            const constructedTitle = [
                (created_at_formated && user_created_name) ? `Cadastrado em ${created_at_formated} por ${user_created_name}` : null,
                (updated_at_formated && user_updated_name) ? `Última atualização em ${updated_at_formated} por ${user_updated_name}` : null
            ].filter(Boolean).join(' - ');

            setTitle(constructedTitle)

            setProject(data.project_selected)
            setResale(data.resale_selected);

            if (data.permissions) {
                setPermissions(data.permissions.split(","))
            }

            const groupFind = groupSshOptions.find(g => g.value === data.group_ssh);
            setGroupSsh(groupFind);
            const lastAccessFormated = GeneralUtils.formatDate(data.last_access);
            setLastAccess(lastAccessFormated);

            if (data.cpf) {
                const validateCpf = GeneralUtils.validateCpf(data.cpf);
                setCpfValidate(validateCpf);
            }

            Object.keys(data).forEach(key => data[key] = data[key] ?? "");

            data.password = "";

            setData(data);

            return data;
        }),
        queryKey: ['user', {id}],
        enabled: !!id,
        initialData,
        throwOnError: (error) => {
            console.error(error)
        },
        retry: 0
    });

    const submit = (newEdit) => {
        setIsLoading(true);

        const formData = new FormData();
        Object.keys(data).forEach(key => {
            if (key === "permissions") return;

            formData.append(key, data[key]);
        });

        formData.append("project_id", project?.value || "");
        formData.append("resale_id", resale?.value || "");
        formData.append("group_ssh", groupSsh?.value || "");

        if (canEditPermissions && permissions) {
            const permissionsStr = removeInvalidDependencies(permissions).join(",");
            formData.append("permissions", permissionsStr);
        }

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? UserService.edit(formData, id) : UserService.create(formData);

        response
            .then(({data}) => {
                refetch();
                toast.success("Salvo com sucesso.");
                if (newEdit === "save_and_new"){
                    setData(initialData);
                    setProject("");
                    setCpfValidate(null);
                    setLastAccess("");
                    setResale("");
                    setGroupSsh("");
                    setPermissions([]);
                    navigate(`/users/form`);
                }else{
                    navigate(`/users/form/${data.id}`);
                }
            })
            .catch(error => {
                const {data: {message}} = error.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false));
    }

    const deleteUser = () => {

        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir o usuário?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);
                userService.delete(id)
                    .then(() => {
                        toast.success("Excluído com sucesso.");
                        navigate("/users");
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const removeInvalidDependencies = (selectedPermissions) => {
        let nextPermissions = [...selectedPermissions];
        let hasChanges = true;

        while (hasChanges) {
            hasChanges = false;
            const validPermissions = [];

            for (const permissionKey of nextPermissions) {
                const permission = permissionsByKey[permissionKey];
                const dependsOn = permission?.dependsOn ?? [];
                let hasDependencySelected = !dependsOn.length;

                for (const dependency of dependsOn) {
                    if (nextPermissions.includes(dependency)) {
                        hasDependencySelected = true;
                        break;
                    }
                }

                if (!hasDependencySelected) hasChanges = true;
                else validPermissions.push(permissionKey);
            }

            nextPermissions = validPermissions;
        }

        return nextPermissions;
    }

    const permissionSelection = (targetPermission) => {
        const permissionFind = permissions.find(p => p === targetPermission);
        let nextPermissions = [...permissions];

        if (permissionFind) {
            nextPermissions = nextPermissions.filter(permission => permission !== targetPermission);

            if (targetPermission === "alter_status") {
                nextPermissions = nextPermissions.filter(permission => permission !== "supervisor");
            }
        } else {
            if (targetPermission === "user_project") {
                nextPermissions = ["public_access", "user_project"]
            }else if (targetPermission === "attendances") {
                nextPermissions.push("attendances", "alter_status")
            } else {
                nextPermissions = nextPermissions
                    .filter(permission => !(mutuallyExclusivePermissions[targetPermission] ?? []).includes(permission));
                nextPermissions.push(targetPermission);
            }
        }

        setPermissions([...new Set(removeInvalidDependencies(nextPermissions))]);
    }

    const checkPermission = (key) => {
        let disabled = false;
        const permission = permissionsByKey[key];
        const dependsOn = permission?.dependsOn ?? [];

        if (dependsOn.length && !dependsOn.some(dependency => permissions.includes(dependency))) {
            disabled = true;
        }

        if (
            !permissions.includes(key)
            && (mutuallyExclusivePermissions[key] ?? []).some(permission => permissions.includes(permission))
        ) {
            disabled = true;
        }

        if (key === "supervisor" && !permissions.includes("alter_status")) {
            disabled = true;
        }

        if (key === "user_project" && !permissions.includes("public_access")) {
            disabled = true;
        }

        if (permissions.includes("user_project") && !["public_access", "user_project"].includes(key)) {
            disabled = true;
        }

        if (key === "request_only_my_user" && permissions.includes("request")){
            disabled = true;
        }

        if (key === "request" && permissions.includes("request_only_my_user")){
            disabled = true;
        }

        return disabled;
    }

    const renderPermission = ({key, name, description, dependsOn = []}, index) => {
        const isChecked = permissions.includes(key);
        const disabled = checkPermission(key);
        const hasTreeLine = !!dependsOn.length;

        return (
            <div
                className={[
                    "relative mt-1 px-2 py-1.5",
                    hasTreeLine ? "ml-5 border-l border-gray-300 pl-4 before:absolute before:left-0 before:top-4 before:h-px before:w-3 before:bg-gray-300" : "",
                    disabled ? "text-gray-400" : "",
                ].filter(Boolean).join(" ")}
                key={`${key}-${index}`}
            >
                <InputCheckbox
                    checked={isChecked}
                    col={""}
                    label={name}
                    id={key}
                    onChange={() => permissionSelection(key)}
                    disabled={disabled}
                />

                {!!description && (
                    <p className="ml-6 mt-0.5 text-xs leading-4 text-gray-500">
                        {description}
                    </p>
                )}
            </div>
        );
    }

    const renderPermissionTree = (groupPermissions) => {
        const childrenByParent = groupPermissions.reduce((groups, permission) => {
            const parentKey = permission.dependsOn?.find(dependency =>
                groupPermissions.some(groupPermission => groupPermission.key === dependency)
            );

            if (!parentKey) return groups;

            if (!groups[parentKey]) groups[parentKey] = [];
            groups[parentKey].push(permission);

            return groups;
        }, {});

        const renderPermissionBranch = (permission, index) => (
            <React.Fragment key={permission.key}>
                {renderPermission(permission, index)}
                {(childrenByParent[permission.key] ?? []).map(renderPermissionBranch)}
            </React.Fragment>
        );

        return groupPermissions
            .filter((permission, index, groupItems) => (
                groupItems.findIndex(item => item.key === permission.key) === index
            ))
            .filter(permission => {
                const hasParentInGroup = permission.dependsOn?.some(dependency =>
                    groupPermissions.some(groupPermission => groupPermission.key === dependency)
                );

                return !hasParentInGroup;
            })
            .map(renderPermissionBranch);
    }

    const renderPermissionGroup = ({key, title}) => {
        const groupPermissions = permissionsList.filter(permission => permission.group === key);

        return (
            <div className="lg:col-span-4" key={key}>
                <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2">
                    <h3>{title}</h3>
                </div>

                {renderPermissionTree(groupPermissions)}
            </div>
        );
    }

    const validateCpf = (e) => {
        const {value} = e.target;
        const cpf = GeneralUtils.validateCpf(value);
        setCpfValidate(cpf);
    }

    const handleInput = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}));
    const handleInputMask = ({target: {value, id}}) => {
        const clearValue = value.replace(/[.,\s]/g, "");
        setData(prev => ({...prev, [id]: clearValue}));
    }

    return (
        <TemplateDefault title="Usuários" isLoading={isLoading}>
            <Card title={title ?? "Dados"} headerClass={title ? "text-center" : ""} titleClass={title ? "text-sm" : "text-lg"} wrapperClass={"relative"}>
                {
                    title &&
                    <div className={"flex items-center text-lg text-lg absolute top-[5px]"}>
                        Dados
                    </div>
                }
                <div className={"flex justify-between"}>
                    {
                        lastAccess ? (
                                <span className={"mb-4"}>Data do último acesso <span
                                    className={"font-bold"}>{lastAccess}</span>
                                </span>
                            )
                            :
                            (<span></span>)

                    }
                    {
                        groupSsh &&
                        <span className={"mb-4"}>Grupo SSH: <span
                            className={"font-bold"}>{groupSsh.label}</span>
                        </span>
                    }
                </div>

                <div className="grid lg:grid-cols-12 gap-4 col-span-2">
                    <InputForm value={data.name} onChange={handleInput} id="name" name="name" label="Nome *"
                               col="lg:col-span-7" disabled={cantEditFields}/>

                    <div className={"relative lg:col-span-3"}>
                        {
                            cpfValidate === false &&
                            <span className={"text-red-600 absolute top-[-19px] right-0"}>CPF inválido</span>
                        }
                        <InputForm onBlur={validateCpf} onChange={handleInput} value={data.cpf} mask={MaskDefault.cpf}
                                   id="cpf" name="cpf" label="CPF" disabled={cantEditFields}/>
                    </div>

                    <InputForm value={data.phone} mask={MaskDefault.phone} id="phone" disabled={cantEditFields}
                               name="phone" onBlur={handleInputMask}
                               label="Telefone" col="lg:col-span-2" whatsApp={true}/>
                    <InputForm value={data.email} onChange={handleInput} id="email" name="email" label="E-mail"
                               col="lg:col-span-4" disabled={cantEditFields}/>

                    <InputForm value={data.second_phone} mask={MaskDefault.phone} id="second_phone" disabled={cantEditFields}
                               name="second_phone" onBlur={handleInputMask}
                               label="Telefone secundário" col="lg:col-span-2" whatsApp={true}/>
                    <div className="lg:col-span-6 text-gray-900">
                        <SelectComponent
                            instanceId="project"
                            label="Projeto"
                            options={projects}
                            value={project}
                            onChange={(value) => setProject(value)}
                            isDisabled={cantEditFields}
                        />
                    </div>
                    <div className="lg:col-span-5 text-gray-900">
                        <SelectComponent
                            instanceId="resale"
                            label="Revenda"
                            options={resales}
                            value={resale}
                            onChange={(value) => setResale(value)}
                            isDisabled={cantEditFields}
                        />
                    </div>


                    <div className={"flex gap-4 col-span-7"}>
                        { canEditSsh &&
                            <SelectComponent
                                instanceId="group_ssh"
                                label="Grupo SSH"
                                options={groupSshOptions}
                                value={groupSsh}
                                onChange={(value) => setGroupSsh(value)}
                                isDisabled={cantEditFields}
                                col={"text-gray-900 w-full"}
                            />
                        }
                        <InputCheckbox id={"is_commercial"} name={"is_commercial"} label={"Contato comercial"} col={"w-[177px] text-center"}
                                       onChange={({target: {checked}}) => setData(prev => ({
                                           ...prev,
                                           is_commercial: checked
                                       }))}
                                       checked={data.is_commercial}
                                       disabled={cantEditFields}
                        />
                    </div>

                    <InputForm value={data.login} onChange={handleInput} id="login" name="login" label="Login"
                               col="lg:col-span-3" disabled={cantEditFields}/>
                    <InputForm value={data.password} onChange={handleInput} id="password" name="password" label="Senha"
                               col="lg:col-span-2" type={"password"} disabled={cantEditFields}/>
                    <InputForm onChange={handleInput} id="gitlab_user" name="gitlab_user" label="Usuário Gitlab"
                               value={data.gitlab_user} col="lg:col-span-2" disabled={cantEditFields}/>
                    <div className={"flex gap-4 col-span-4"}>
                        <InputForm onChange={handleInput} id="team" name="team" label="Time Gitlab" col={"w-[80%]"}
                                   value={data.team} disabled={cantEditFields}/>

                        <InputCheckbox
                            id="team_leader"
                            name="team_leader"
                            label="Lider de time"
                            col={"w-[120px] text-center"}
                            onChange={({target: {checked}}) => setData(prev => ({
                                ...prev,
                                team_leader: checked
                            }))}
                            checked={data.team_leader}
                            disabled={cantEditFields}
                        />
                    </div>


                    <InputCheckbox
                        id="is_inactive"
                        name="is_inactive"
                        label="Inativar"
                        col={"col-span-1"}
                        onChange={({target: {checked}}) => setData(prev => ({
                            ...prev,
                            is_inactive: checked
                        }))}
                        checked={data.is_inactive}
                        disabled={cantEditFields}
                    />

                </div>

                {
                    canEditPermissions &&
                    <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-12 mt-4">
                        <div className="col-span-12 text-gray-900">
                            <h3>Permissões</h3>
                            <hr className={"border-gray-300"}/>
                        </div>

                        {permissionGroups.map(renderPermissionGroup)}
                    </div>



                }
            </Card>

            <Card wrapperClass="mt-5">
                <div className="flex justify-end gap-3">
                    {
                        !cantEditFields &&
                        <Button onClick={() => submit('save_and_new')} type="submit" target="primary">
                            Salvar e adicionar novo
                        </Button>
                    }
                    {
                        !cantEditFields && id &&

                        <Button onClick={deleteUser} target="danger">
                            Excluir
                        </Button>
                    }
                    <Button onClick={() => navigate("/users")} target="warning">
                        Cancelar
                    </Button>
                    {
                        !cantEditFields &&
                        <Button onClick={() => submit('save_and_edit')} type="submit" target="success">
                            Salvar
                        </Button>
                    }
                </div>

            </Card>
        </TemplateDefault>
    );
};

export default UsersForm;
